/**
 * Color manipulation utilities for dynamic theming
 */

/**
 * Validates if a string is a valid 6-digit hex color code
 */
export function validateHexColor(hex: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

/**
 * Converts hex color to RGB object
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  if (!validateHexColor(hex)) {
    return null;
  }

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Converts RGB to hex color
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => {
    const hex = Math.round(Math.max(0, Math.min(255, x))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

/**
 * Converts hex color to HSL
 */
export function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Converts HSL to hex color
 */
export function hslToHex(h: number, s: number, l: number): string {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return rgbToHex(r * 255, g * 255, b * 255);
}

/**
 * Lightens a hex color by a percentage
 */
export function lightenColor(hex: string, percent: number): string {
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;

  hsl.l = Math.min(100, hsl.l + percent);
  return hslToHex(hsl.h, hsl.s, hsl.l);
}

/**
 * Darkens a hex color by a percentage
 */
export function darkenColor(hex: string, percent: number): string {
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;

  hsl.l = Math.max(0, hsl.l - percent);
  return hslToHex(hsl.h, hsl.s, hsl.l);
}

/**
 * Shifts the hue of a hex color by degrees
 */
export function shiftHue(hex: string, degrees: number): string {
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;

  hsl.h = (hsl.h + degrees + 360) % 360;
  return hslToHex(hsl.h, hsl.s, hsl.l);
}

/**
 * Generates all color variants from a base accent color
 */
export function generateColorVariants(baseHex: string): Record<string, string> {
  // Validate the base color
  if (!validateHexColor(baseHex)) {
    console.warn(`Invalid accent color "${baseHex}", using default #9333ea`);
    baseHex = '#9333ea';
  }

  const rgb = hexToRgb(baseHex);
  if (!rgb) {
    console.warn(`Failed to parse accent color "${baseHex}", using default #9333ea`);
    baseHex = '#9333ea';
  }

  const { r, g, b } = hexToRgb(baseHex)!;

  return {
    '--accent': baseHex,
    '--accent-hover': lightenColor(baseHex, 8),
    '--accent-light': lightenColor(baseHex, 8),
    '--accent-lighter': lightenColor(baseHex, 20),
    '--accent-dark': darkenColor(baseHex, 12),
    '--accent-purple': shiftHue(baseHex, -10),
    
    // Alpha/opacity variants
    '--accent-alpha-8': `rgba(${r}, ${g}, ${b}, 0.08)`,
    '--accent-alpha-10': `rgba(${r}, ${g}, ${b}, 0.1)`,
    '--accent-alpha-12': `rgba(${r}, ${g}, ${b}, 0.12)`,
    '--accent-alpha-15': `rgba(${r}, ${g}, ${b}, 0.15)`,
    '--accent-alpha-20': `rgba(${r}, ${g}, ${b}, 0.2)`,
    '--accent-alpha-25': `rgba(${r}, ${g}, ${b}, 0.25)`,
    '--accent-alpha-30': `rgba(${r}, ${g}, ${b}, 0.3)`,
    '--accent-alpha-35': `rgba(${r}, ${g}, ${b}, 0.35)`,
    '--accent-alpha-40': `rgba(${r}, ${g}, ${b}, 0.4)`,
    '--accent-alpha-45': `rgba(${r}, ${g}, ${b}, 0.45)`,
    '--accent-alpha-50': `rgba(${r}, ${g}, ${b}, 0.5)`,
    '--accent-alpha-60': `rgba(${r}, ${g}, ${b}, 0.6)`,
    '--accent-alpha-70': `rgba(${r}, ${g}, ${b}, 0.7)`,
  };
}

/**
 * Applies theme colors dynamically to the document
 */
export function applyThemeColors(accentColor: string): void {
  const variants = generateColorVariants(accentColor);
  
  // Apply CSS variables to :root
  const root = document.documentElement;
  Object.entries(variants).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });

  // Update meta theme-color tag
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', accentColor);
  } else {
    // Create meta tag if it doesn't exist
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = accentColor;
    document.head.appendChild(meta);
  }

  console.log(`Applied theme color: ${accentColor}`);
}
